"use client";
/**
 * The podcast archive list — every episode, searchable.
 *
 * Markup/classes mirror MediaLibrary (chapters.css + experience.css) so the
 * archive reads identically on /podcast and /media. Two differences, both
 * deliberate: there is no type tab-bar (everything here is an episode), and
 * search matches TOPICS as well as the title, because an agent looks for
 * "rapid disenrollment", not the episode's name.
 */
import { useMemo, useState } from "react";
import Link from "next/link";

export type EpisodeEntry = {
    path: string;
    title: string;
    date: string;
    episodeNumber?: number;
    topics: string[];
};

const PAGE_SIZE = 12;

const SearchIcon = ({ size = 18 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
);
const Play = ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 3v18l15-9z" /></svg>
);
const ArrowUpRight = ({ size = 16, className }: { size?: number; className?: string }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7" /><path d="M7 7h10v10" /></svg>
);

export default function PodcastArchive({ entries }: { entries: EpisodeEntry[] }) {
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);

    const result = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return entries;
        return entries.filter(
            (e) =>
                e.title.toLowerCase().includes(q) ||
                e.topics.some((t) => t.toLowerCase().includes(q)),
        );
    }, [entries, query]);

    const pages = Math.ceil(result.length / PAGE_SIZE);
    const start = (page - 1) * PAGE_SIZE;

    return (
        <section className="media-library section-pad" id="episodes">
            <div className="section-label mono">
                EVERY EPISODE / {entries.length} CONVERSATIONS
            </div>
            <div className="directory-controls">
                <label className="search-field">
                    <SearchIcon size={18} />
                    <input
                        aria-label="Search episodes by topic, guest, or title"
                        placeholder="Search a topic, guest, or episode"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setPage(1);
                        }}
                    />
                </label>
            </div>
            <div aria-live="polite" className="results-count mono">
                {result.length} RESULTS
            </div>
            {/*
              EVERY episode is rendered into the DOM; pagination only HIDES the
              rows outside the current window. Slicing the array instead left just
              12 of 133 episodes with a crawlable link from this page — the other
              121 existed only in the sitemap, which is discovery, not authority.
              Link equity flows through links, so they all have to be here.
            */}
            <div className="media-list">
                {result.map((item, i) => (
                    <Link
                        className="media-row"
                        hidden={i < start || i >= start + PAGE_SIZE}
                        style={{ animationDelay: `${(i % PAGE_SIZE) * 25}ms` }}
                        href={item.path}
                        key={item.path}
                    >
                        <span className="media-row-index" aria-hidden="true">
                            {item.episodeNumber
                                ? String(item.episodeNumber).padStart(3, "0")
                                : String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="media-row-type mono">
                            <Play size={16} /> EPISODE
                        </span>
                        <h3>{item.title}</h3>
                        <span className="media-row-date mono">
                            {item.date || "AGENT BOOST"}
                        </span>
                        <ArrowUpRight className="row-arrow" />
                    </Link>
                ))}
            </div>
            {result.length === 0 && (
                <div className="empty-state">
                    <h3>No matches yet.</h3>
                    <p>Try a broader phrase, a guest name, or a carrier.</p>
                    <button
                        type="button"
                        className="button"
                        onClick={() => setQuery("")}
                    >
                        Show every episode
                    </button>
                </div>
            )}
            {pages > 1 && (
                <nav className="media-pagination" aria-label="Archive pages">
                    <button
                        type="button"
                        className="button"
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        Previous
                    </button>
                    <span className="mono">
                        PAGE {page} / {pages}
                    </span>
                    <button
                        type="button"
                        className="button"
                        disabled={page === pages}
                        onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    >
                        Next
                    </button>
                </nav>
            )}
        </section>
    );
}
